import { useCallback, useEffect, useState } from "react";
import { graphql,useMutation } from "react-relay";

import { useTeamSelectionSetUserTeamContextMutation } from "./__generated__/useTeamSelectionSetUserTeamContextMutation.graphql";

// Define mutation
const setUserTeamContextMutation = graphql`
  mutation useTeamSelectionSetUserTeamContextMutation($input: SetUserTeamContextInput!) {
    setUserTeamContext(input: $input) {
      userTeamContext {
        id
        team {
          id
          name
        }
      }
      error
    }
  }
`;

// Define type for team context from backend
export interface TeamContextType {
  id: string;
  team: {
    id: string;
    name: string;
  };
}

// Props for the hook
export interface UseTeamSelectionProps {
  userId: string;
  initialTeamContext?: TeamContextType | null;
}

/**
 * Hook for managing team selection across the application.
 * Uses backend-persisted team context with localStorage migration support.
 *
 * @param props - Contains userId and optional initialTeamContext from backend
 * @returns An object containing team selection state and functions
 */
export function useTeamSelection({ userId, initialTeamContext }: UseTeamSelectionProps) {
  // Initialize from backend data
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(
    initialTeamContext?.team?.id ?? null
  );
  const [selectedTeamName, setSelectedTeamName] = useState<string | null>(
    initialTeamContext?.team?.name ?? null
  );

  const [commitSetContext, isInFlight] =
    useMutation<useTeamSelectionSetUserTeamContextMutation>(setUserTeamContextMutation);

  // Migration: Check localStorage and migrate to database if needed
  useEffect(() => {
    const localStorageKey = `tz-team:${userId}`;
    const storedValue = localStorage.getItem(localStorageKey);

    if (storedValue && !initialTeamContext) {
      try {
        const parsed = JSON.parse(storedValue);
        if (parsed.teamId) {
          // Migrate to database
          commitSetContext({
            variables: { input: { teamId: parsed.teamId } },
            onCompleted: (response) => {
              if (response.setUserTeamContext?.userTeamContext) {
                // Clear localStorage after successful migration
                localStorage.removeItem(localStorageKey);
                setSelectedTeamId(parsed.teamId);
                setSelectedTeamName(parsed.teamName);
              }
            },
            onError: (error) => {
              console.error("Failed to migrate team selection:", error);
            },
          });
        }
      } catch (e) {
        console.error("Failed to parse localStorage team selection:", e);
      }
    }
  }, [userId, initialTeamContext, commitSetContext]);

  // Select team and persist to backend
  const selectTeam = useCallback(
    (teamId: string, teamName: string): Promise<boolean> => {
      return new Promise((resolve) => {
        // Snapshot current selection so we can revert accurately on error
        const previousTeamId = selectedTeamId;
        const previousTeamName = selectedTeamName;

        // Optimistic update
        setSelectedTeamId(teamId);
        setSelectedTeamName(teamName);

        // Persist to backend
        commitSetContext({
          variables: { input: { teamId } },
          onCompleted: (response) => {
            if (response.setUserTeamContext?.error) {
              console.error("Failed to set team context:", response.setUserTeamContext.error);
              // Revert on error
              setSelectedTeamId(previousTeamId);
              setSelectedTeamName(previousTeamName);
              resolve(false);
            } else {
              resolve(true);
            }
          },
          onError: (error) => {
            console.error("Failed to set team context:", error);
            // Revert on error
            setSelectedTeamId(previousTeamId);
            setSelectedTeamName(previousTeamName);
            resolve(false);
          },
        });
      });
    },
    [commitSetContext, selectedTeamId, selectedTeamName]
  );

  // Clear team selection to show all teams
  const clearTeamSelection = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      // Snapshot current selection so we can revert accurately on error
      const previousTeamId = selectedTeamId;
      const previousTeamName = selectedTeamName;

      // Optimistic update
      setSelectedTeamId(null);
      setSelectedTeamName(null);

      // Persist to backend
      commitSetContext({
        variables: { input: { teamId: null } },
        onCompleted: (response) => {
          if (response.setUserTeamContext?.error) {
            console.error(
              "Failed to clear team context:",
              response.setUserTeamContext.error
            );
            // Revert on error
            setSelectedTeamId(previousTeamId);
            setSelectedTeamName(previousTeamName);
            resolve(false);
          } else {
            // Also clear any remaining localStorage
            const localStorageKey = `tz-team:${userId}`;
            localStorage.removeItem(localStorageKey);
            resolve(true);
          }
        },
        onError: (error) => {
          console.error("Failed to clear team context:", error);
          // Revert on error
          setSelectedTeamId(previousTeamId);
          setSelectedTeamName(previousTeamName);
          resolve(false);
        },
      });
    });
  }, [commitSetContext, selectedTeamId, selectedTeamName, userId]);

  return {
    teamId: selectedTeamId,
    teamName: selectedTeamName,
    selectTeam,
    clearTeamSelection,
    isInFlight,
  };
}
